// Shipper Portal — KAN-574 (RFQ quoting) + KAN-606 (account conversion + history)
// Magic-link flow for one-time RFQ quoting; persistent account flow for history access.

import { mountBuildStamp } from '../build-stamp.js';   // KAN-824
import { mountUpdateBanner } from '../update-banner.js';   // KAN-899

// KAN-824: mount the build stamp FIRST, before anything that can throw. A broken portal is exactly
// when you most need to know which build the carrier is looking at.
mountBuildStamp();
mountUpdateBanner();

const SUPABASE_URL  = window.__SUPABASE_URL__;
const SUPABASE_ANON = window.__SUPABASE_ANON__;

// KAN-825: this portal was dead in production for weeks and nobody could tell why.
// `createClient('')` throws "supabaseUrl is required." — and because that ran at MODULE TOP LEVEL,
// the throw killed the entire module: no render, no auth, no error. Carriers just saw the "Loading…"
// placeholder forever, with the real cause buried in the console.
//
// The config comes from %VITE_SUPABASE_URL% tokens substituted into shipper-portal/index.html at
// build time; when the build environment lacks those vars they silently become ''. So: validate, and
// fail LOUDLY and visibly rather than hanging.
function _renderFatalConfigError() {
  const root = document.getElementById('shipper-app') || document.body;
  root.innerHTML = `
    <div style="max-width:520px;margin:12vh auto;padding:24px;border:1px solid #fca5a5;
                background:#fef2f2;border-radius:10px;font-family:system-ui,sans-serif">
      <h2 style="margin:0 0 8px;font-size:18px;color:#991b1b">Portal unavailable</h2>
      <p style="margin:0 0 12px;font-size:14px;color:#7f1d1d;line-height:1.5">
        This portal is not configured correctly and cannot connect. This is a problem on our side —
        nothing you did. Please contact your Supersede representative.
      </p>
      <p style="margin:0;font-size:12px;color:#b91c1c">
        Reference: missing Supabase configuration · build ${window.BUILD_ID || 'unknown'}
      </p>
    </div>`;
}

const _configOk = !!SUPABASE_URL && !!SUPABASE_ANON;
if (!_configOk) {
  console.error('[Shipper Portal] Missing Supabase config — VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY ' +
    'were not substituted at build time (they resolve to empty strings). The portal cannot start.');
}

// Supabase client — used for auth (signInWithPassword, getSession) and history queries
const _sb = _configOk ? window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON) : null;

// ────────────────────────────────────────────────────────────────────────────
// Render helpers — static state cards
// ────────────────────────────────────────────────────────────────────────────

function renderLoading(root) {
  root.innerHTML = `
    <div class="portal-loading">
      <div class="spinner"></div>
      <p>Loading…</p>
    </div>`;
}

const ERROR_COPY = {
  VOIDED:       'This link is no longer valid. The RFQ has been revised — check your email for an updated link.',
  EXPIRED:      'This RFQ has closed. The quote deadline has passed.',
  // KAN-590: distinct from EXPIRED (quote deadline) — the magic link itself is
  // only valid for 7 days from send, independent of the RFQ's own status.
  LINK_EXPIRED: 'This quote link has expired. Contact Supersede to request a new link.',
  CANCELLED:    'This RFQ has been cancelled by the shipper. No response needed.',
  NOT_FOUND:    'This link could not be found. Please check your email.',
};

function renderError(root, code, customMsg) {
  const msg = customMsg
    || ERROR_COPY[String(code).toUpperCase()]
    || 'Something went wrong. Please try again or contact the shipper.';
  root.innerHTML = `
    <div class="portal-card portal-state-card portal-error-card">
      <div class="portal-logo">
        <img src="/logo.svg" alt="Supersede" width="40" height="40">
        <span class="portal-brand">Supersede</span>
      </div>
      <div class="state-icon state-icon--error">&#9888;</div>
      <p class="state-msg">${escHtml(msg)}</p>
      <p class="state-hint">Need help? Contact <a href="mailto:freight@supersede.build">freight@supersede.build</a></p>
    </div>`;
}

function renderAlreadyResponded(root, amount, email, quoteHistory) {
  const formatted = (amount != null && !isNaN(Number(amount)))
    ? '$' + Number(amount).toFixed(2)
    : 'your quote';
  root.innerHTML = `
    <div class="portal-card portal-state-card portal-confirmed-card">
      <div class="portal-logo">
        <img src="/logo.svg" alt="Supersede" width="40" height="40">
        <span class="portal-brand">Supersede</span>
      </div>
      <div class="state-icon state-icon--success">&#10003;</div>
      <h2 class="state-heading">Quote Received</h2>
      <p class="state-msg">Thanks! We already received your quote of <strong>${escHtml(formatted)}</strong>. We'll be in touch with the outcome.</p>
      ${email ? renderAccountPromptHtml(email) : ''}
    </div>`
    + renderQuoteHistorySection(quoteHistory);
  if (email) bindAccountPrompt(root);
}

// ────────────────────────────────────────────────────────────────────────────
// Quote history (KAN-592) — only rendered when the server included a
// quoteHistory array in the validate-rfq-token response, i.e. only when
// shippers.allow_history_access is true for this shipper. A null/undefined
// quoteHistory (flag off, or shipper unresolved) renders nothing at all —
// absence of the section is how "not entitled to history" is signaled.
// ────────────────────────────────────────────────────────────────────────────

function renderQuoteHistorySection(quoteHistory) {
  if (!Array.isArray(quoteHistory)) return '';

  const rows = quoteHistory.map((h) => {
    const hRfq   = h.rfqs || {};
    const snap   = hRfq.shipment_snapshot || {};
    const quote  = h.quote_amount_usd != null ? '$' + Number(h.quote_amount_usd).toFixed(2) : '—';
    const outcome = h.status === 'awarded'
      ? '<span style="color:#16a34a;font-weight:700">Won</span>'
      : (hRfq.status === 'awarded' || hRfq.status === 'closed')
      ? '<span style="color:#dc2626">Not selected</span>'
      : '<span style="color:#92400e">Pending</span>';
    return '<tr>'
      + '<td>' + escHtml(hRfq.serial || hRfq.id || '—') + '</td>'
      + '<td>' + escHtml(snap.destination_address || snap.dest_address || '—') + '</td>'
      + '<td>' + (hRfq.created_at ? fmtDate(hRfq.created_at) : '—') + '</td>'
      + '<td>' + quote + '</td>'
      + '<td>' + outcome + '</td>'
      + '</tr>';
  }).join('');

  return '<section class="portal-card" aria-label="Quote history">'
    + '<h2 class="section-heading">Your Quote History</h2>'
    + (rows
      ? '<div style="overflow-x:auto"><table class="detail-table" style="width:100%">'
        + '<thead><tr><th>RFQ #</th><th>Destination</th><th>Date</th><th>Your Quote</th><th>Outcome</th></tr></thead>'
        + '<tbody>' + rows + '</tbody></table></div>'
      : '<p class="state-hint">No previous quotes on file yet.</p>')
    + '</section>';
}

// ────────────────────────────────────────────────────────────────────────────
// Quote form
// ────────────────────────────────────────────────────────────────────────────

function renderQuoteForm(root, rfq, recipient, previousSnapshot, token, shipperEmail, quoteHistory) {
  const snap     = rfq.shipment_snapshot || {};
  const prevSnap = previousSnapshot || null;

  const serial       = rfq.serial || rfq.id || 'N/A';
  const expiresAt    = rfq.expires_at ? new Date(rfq.expires_at) : null;
  // KAN-590: the magic link's own 7-day validity window — distinct from expiresAt
  // (the quote deadline). Surfaced so the shipper knows when this link itself dies.
  const linkExpiresAt = rfq.link_expires_at ? new Date(rfq.link_expires_at) : null;
  const closureMode  = rfq.closure_mode || 'manual';

  const details = [
    { label: 'Origin Plant',         key: 'origin_plant',       display: snap.origin_plant       || snap.plant           || '—' },
    { label: 'Destination',          key: 'destination_address', display: snap.destination_address || snap.dest_address    || '—' },
    { label: 'Pieces / Pallets',     key: 'pieces',             display: fmtPieces(snap)                                      },
    { label: 'Total Weight',         key: 'total_weight_lbs',   display: fmtWeight(snap)                                      },
    { label: 'Requested Ship Date',  key: 'ship_date',          display: fmtDate(snap.ship_date   || snap.requested_ship_date)  },
  ];

  const changedKeys = prevSnap
    ? new Set(
        details
          .filter(d => prevSnap[d.key] != null && String(prevSnap[d.key]) !== String(snap[d.key] ?? ''))
          .map(d => d.key)
      )
    : new Set();

  const detailRows = details.map(d => {
    const changed = changedKeys.has(d.key);
    return '<tr><td class="detail-label">' + escHtml(d.label) + '</td>'
      + '<td class="detail-value' + (changed ? ' detail-value--changed' : '') + '">' + escHtml(String(d.display)) + '</td></tr>';
  }).join('');

  const revBanner = (prevSnap && changedKeys.size > 0)
    ? '<div class="revision-banner"><strong>Revised RFQ</strong> — highlighted fields have changed from the previous version.</div>'
    : '';

  let closureNote = '';
  if (closureMode === 'all_responded') {
    closureNote = 'Closes when all invited shippers respond.';
  } else if ((closureMode === 'timer' || closureMode === 'manual') && expiresAt) {
    closureNote = 'Closes at ' + fmtDatetime(expiresAt) + '.';
  } else if (closureMode === 'manual') {
    closureNote = 'Closes at shipper\'s discretion.';
  }

  const linkExpiryNote = linkExpiresAt
    ? '<p class="closure-note">This quote link is valid until ' + escHtml(fmtDatetime(linkExpiresAt)) + '.</p>'
    : '';

  const countdownId   = 'portal-countdown';
  const countdownHtml = expiresAt
    ? '<div class="countdown" id="' + countdownId + '">&#9201; ' + escHtml(getCountdownText(expiresAt)) + '</div>'
    : '';

  root.innerHTML =
    '<header class="portal-header">'
    + '<div class="portal-logo"><img src="/logo.svg" alt="Supersede" width="36" height="36"><span class="portal-brand">Supersede</span></div>'
    + '<h1 class="portal-title">RFQ #' + escHtml(String(serial)) + ' &mdash; Quote Submission</h1>'
    + '</header>'
    + revBanner
    + '<section class="portal-card" aria-label="Shipment details">'
    + '<h2 class="section-heading">Shipment Details</h2>'
    + '<table class="detail-table"><tbody>' + detailRows + '</tbody></table>'
    + '</section>'
    + countdownHtml
    + '<section class="portal-card" aria-label="Quote form">'
    + '<h2 class="section-heading">Submit Your Quote</h2>'
    + '<form id="quote-form" class="quote-form" novalidate>'
    + '<div class="form-group">'
    + '<label class="form-label" for="quote-amount">Rate (USD) <span class="required" aria-hidden="true">*</span></label>'
    + '<div class="input-prefix-wrap"><span class="input-prefix" aria-hidden="true">$</span>'
    + '<input type="number" id="quote-amount" name="amount" class="form-input" placeholder="0.00" min="0.01" step="0.01" inputmode="decimal" required autocomplete="off" aria-describedby="amount-error">'
    + '</div><div class="form-error" id="amount-error" role="alert" hidden></div></div>'
    + '<div class="form-group">'
    + '<label class="form-label" for="quote-notes">Notes <span class="optional">(optional)</span></label>'
    + '<textarea id="quote-notes" name="notes" class="form-textarea" rows="4" placeholder="Transit time, fuel surcharges, conditions, etc."></textarea>'
    + '</div>'
    + '<div class="form-footer">'
    + '<button type="submit" class="btn-submit" id="submit-btn">Submit Quote</button>'
    + '<div class="submit-error" id="submit-error" role="alert" hidden></div>'
    + '</div></form></section>'
    + (closureNote ? '<p class="closure-note">' + escHtml(closureNote) + '</p>' : '')
    + linkExpiryNote
    + renderQuoteHistorySection(quoteHistory);

  if (expiresAt) {
    const countdownEl = document.getElementById(countdownId);
    const interval = setInterval(() => {
      if (!document.getElementById(countdownId)) { clearInterval(interval); return; }
      countdownEl.textContent = '⏱ ' + getCountdownText(expiresAt);
    }, 60000);
  }

  document.getElementById('quote-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const amountInput = document.getElementById('quote-amount');
    const notesInput  = document.getElementById('quote-notes');
    const amountError = document.getElementById('amount-error');
    const submitError = document.getElementById('submit-error');
    const submitBtn   = document.getElementById('submit-btn');

    const amountRaw = amountInput.value.trim();
    const amount    = parseFloat(amountRaw);
    if (!amountRaw || isNaN(amount) || amount <= 0) {
      amountError.textContent = 'Please enter a valid amount greater than $0.00.';
      amountError.hidden = false;
      amountInput.focus();
      return;
    }
    amountError.hidden = true;
    submitError.hidden = true;
    submitBtn.disabled    = true;
    submitBtn.textContent = 'Submitting…';

    const notes = notesInput.value.trim() || null;
    try {
      const resp   = await fetch(SUPABASE_URL + '/functions/v1/submit-rfq-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, amount, notes }),
      });
      const result = await resp.json();
      if (result.error) {
        submitError.textContent = result.error.message || 'Submission failed. Please try again.';
        submitError.hidden = false;
        submitBtn.disabled    = false;
        submitBtn.textContent = 'Submit Quote';
        return;
      }
      renderSubmitted(root, amount, shipperEmail);
    } catch {
      submitError.textContent = 'Network error. Please check your connection and try again.';
      submitError.hidden = false;
      submitBtn.disabled    = false;
      submitBtn.textContent = 'Submit Quote';
    }
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Post-submission with account creation prompt
// ────────────────────────────────────────────────────────────────────────────

function renderSubmitted(root, amount, shipperEmail) {
  const formatted = '$' + Number(amount).toFixed(2);
  root.innerHTML =
    '<div class="portal-card portal-state-card portal-confirmed-card">'
    + '<div class="portal-logo"><img src="/logo.svg" alt="Supersede" width="40" height="40"><span class="portal-brand">Supersede</span></div>'
    + '<div class="state-icon state-icon--success">&#10003;</div>'
    + '<h2 class="state-heading">Quote Submitted</h2>'
    + '<p class="state-msg">Thank you! Your quote of <strong>' + escHtml(formatted) + '</strong> has been submitted. We\'ll notify you of the outcome.</p>'
    + '</div>'
    + (shipperEmail ? renderAccountPromptHtml(shipperEmail) : '');
  if (shipperEmail) bindAccountPrompt(root);
}

// ────────────────────────────────────────────────────────────────────────────
// Account creation prompt (shown after quote submit / already-responded)
// ────────────────────────────────────────────────────────────────────────────

function renderAccountPromptHtml(email) {
  return '<section class="portal-card account-prompt" id="account-prompt-section" aria-label="Create portal account">'
    + '<h2 class="section-heading">Create a Portal Account</h2>'
    + '<p class="state-hint" style="margin-bottom:16px">Save your quote history and access past RFQs by creating a free account. Your email is pre-filled from your shipper profile.</p>'
    + '<form id="account-form" class="quote-form" novalidate>'
    + '<div class="form-group">'
    + '<label class="form-label" for="account-email">Email</label>'
    + '<input type="email" id="account-email" class="form-input" value="' + escHtml(email) + '" readonly style="background:var(--g50,#f8fafc);color:var(--g500,#6b7280)">'
    + '</div>'
    + '<div class="form-group">'
    + '<label class="form-label" for="account-password">Password <span class="required">*</span></label>'
    + '<input type="password" id="account-password" class="form-input" placeholder="At least 8 characters" minlength="8" required autocomplete="new-password">'
    + '</div>'
    + '<div class="form-group">'
    + '<label class="form-label" for="account-password2">Confirm Password <span class="required">*</span></label>'
    + '<input type="password" id="account-password2" class="form-input" placeholder="Repeat password" required autocomplete="new-password">'
    + '</div>'
    + '<div class="form-footer">'
    + '<button type="submit" class="btn-submit" id="account-submit-btn">Create Account</button>'
    + '<div class="submit-error" id="account-error" role="alert" hidden></div>'
    + '</div>'
    + '</form>'
    + '<p class="state-hint" style="margin-top:12px">Already have an account? <a href="/" id="go-login-link" style="color:#2563eb">Sign in</a></p>'
    + '</section>';
}

function bindAccountPrompt(root) {
  const form = document.getElementById('account-form');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const pw   = document.getElementById('account-password').value;
    const pw2  = document.getElementById('account-password2').value;
    const errEl = document.getElementById('account-error');
    const btn   = document.getElementById('account-submit-btn');

    if (pw.length < 8) {
      errEl.textContent = 'Password must be at least 8 characters.';
      errEl.hidden = false; return;
    }
    if (pw !== pw2) {
      errEl.textContent = 'Passwords do not match.';
      errEl.hidden = false; return;
    }
    errEl.hidden = true;
    btn.disabled = true; btn.textContent = 'Creating…';

    // Get token from URL — needed to verify the shipper identity server-side
    const token = new URLSearchParams(window.location.search).get('token');
    try {
      const resp   = await fetch(SUPABASE_URL + '/functions/v1/convert-shipper-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: pw }),
      });
      const result = await resp.json();
      if (result.error || !result.success) {
        const code = result.error?.code || '';
        errEl.textContent = code === 'EMAIL_EXISTS'
          ? 'An account with this email already exists. Please sign in instead.'
          : code === 'ACCOUNT_EXISTS'
          ? 'A portal account already exists for this shipper.'
          : result.error?.message || 'Account creation failed. Please try again.';
        errEl.hidden = false;
        btn.disabled = false; btn.textContent = 'Create Account';
        return;
      }
      // Sign in immediately
      const email = document.getElementById('account-email').value;
      await _sb.auth.signInWithPassword({ email, password: pw });
      document.getElementById('account-prompt-section').innerHTML =
        '<div class="state-icon state-icon--success" style="font-size:32px;margin:0 auto 12px">&#10003;</div>'
        + '<h2 class="state-heading">Account Created!</h2>'
        + '<p class="state-hint">You\'re now signed in. <a href="/" style="color:#2563eb">View your quote history</a>.</p>';
    } catch {
      errEl.textContent = 'Network error. Please try again.';
      errEl.hidden = false;
      btn.disabled = false; btn.textContent = 'Create Account';
    }
  });

  const goLogin = document.getElementById('go-login-link');
  if (goLogin) goLogin.addEventListener('click', (e) => { e.preventDefault(); renderLoginForm(root); });
}

// ────────────────────────────────────────────────────────────────────────────
// Login form (shown when no token in URL, or user navigates to /)
// ────────────────────────────────────────────────────────────────────────────

function renderLoginForm(root) {
  root.innerHTML =
    '<header class="portal-header">'
    + '<div class="portal-logo"><img src="/logo.svg" alt="Supersede" width="36" height="36"><span class="portal-brand">Supersede</span></div>'
    + '<h1 class="portal-title">Shipper Portal</h1>'
    + '</header>'
    + '<section class="portal-card" style="max-width:420px;margin:0 auto" aria-label="Sign in">'
    + '<h2 class="section-heading">Sign In</h2>'
    + '<p class="state-hint" style="margin-bottom:16px">Sign in to view your RFQ history and past quotes.</p>'
    + '<form id="login-form" class="quote-form" novalidate>'
    + '<div class="form-group">'
    + '<label class="form-label" for="login-email">Email</label>'
    + '<input type="email" id="login-email" class="form-input" placeholder="dispatch@yourcompany.com" required autocomplete="email">'
    + '</div>'
    + '<div class="form-group">'
    + '<label class="form-label" for="login-password">Password</label>'
    + '<input type="password" id="login-password" class="form-input" placeholder="Your password" required autocomplete="current-password">'
    + '</div>'
    + '<div class="form-footer">'
    + '<button type="submit" class="btn-submit" id="login-btn">Sign In</button>'
    + '<div class="submit-error" id="login-error" role="alert" hidden></div>'
    + '</div>'
    + '</form>'
    + '<p class="state-hint" style="margin-top:8px;text-align:center"><a href="#" id="forgot-pw-link" style="color:#2563eb">Forgot password?</a></p>'
    + '<p class="state-hint" style="margin-top:8px;text-align:center">No account? Use the link in your RFQ email to get started.</p>'
    + '</section>';

  document.getElementById('login-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const email    = document.getElementById('login-email').value.trim();
    const password = document.getElementById('login-password').value;
    const errEl    = document.getElementById('login-error');
    const btn      = document.getElementById('login-btn');

    if (!email || !password) {
      errEl.textContent = 'Please enter your email and password.';
      errEl.hidden = false; return;
    }
    errEl.hidden = true;
    btn.disabled = true; btn.textContent = 'Signing in…';

    const { error } = await _sb.auth.signInWithPassword({ email, password });
    if (error) {
      errEl.textContent = 'Invalid email or password.';
      errEl.hidden = false;
      btn.disabled = false; btn.textContent = 'Sign In';
      return;
    }
    await renderHistory(root);
  });

  document.getElementById('forgot-pw-link').addEventListener('click', (e) => {
    e.preventDefault();
    renderForgotPasswordForm(root);
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Forgot password form (KAN-858)
// ────────────────────────────────────────────────────────────────────────────

function renderForgotPasswordForm(root) {
  root.innerHTML =
    '<header class="portal-header">'
    + '<div class="portal-logo"><img src="/logo.svg" alt="Supersede" width="36" height="36"><span class="portal-brand">Supersede</span></div>'
    + '<h1 class="portal-title">Shipper Portal</h1>'
    + '</header>'
    + '<section class="portal-card" style="max-width:420px;margin:0 auto" aria-label="Reset password">'
    + '<h2 class="section-heading">Reset Password</h2>'
    + '<p class="state-hint" style="margin-bottom:16px">Enter your email address and we\'ll send you a reset link.</p>'
    + '<form id="forgot-form" class="quote-form" novalidate>'
    + '<div class="form-group">'
    + '<label class="form-label" for="forgot-email">Email</label>'
    + '<input type="email" id="forgot-email" class="form-input" placeholder="dispatch@yourcompany.com" required autocomplete="email">'
    + '</div>'
    + '<div class="form-footer">'
    + '<button type="submit" class="btn-submit" id="forgot-btn">Send Reset Link</button>'
    + '<div class="submit-error" id="forgot-error" role="alert" hidden></div>'
    + '</div>'
    + '</form>'
    + '<p class="state-hint" style="margin-top:14px;text-align:center"><a href="#" id="back-to-login" style="color:#2563eb">Back to sign in</a></p>'
    + '</section>';

  document.getElementById('forgot-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = document.getElementById('forgot-email').value.trim();
    const errEl = document.getElementById('forgot-error');
    const btn   = document.getElementById('forgot-btn');
    if (!email) { errEl.textContent = 'Please enter your email address.'; errEl.hidden = false; return; }
    errEl.hidden = true;
    btn.disabled = true; btn.textContent = 'Sending…';
    // redirectTo determines the link destination and the email template branding in auth-email
    await _sb.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin + '/' });
    // Always show success to avoid email enumeration
    root.innerHTML =
      '<div class="portal-card portal-state-card">'
      + '<div class="portal-logo"><img src="/logo.svg" alt="Supersede" width="40" height="40"><span class="portal-brand">Supersede</span></div>'
      + '<div class="state-icon state-icon--success">&#10003;</div>'
      + '<h2 class="state-heading">Check Your Email</h2>'
      + '<p class="state-msg">If an account exists for <strong>' + escHtml(email) + '</strong>, you\'ll receive a reset link shortly.</p>'
      + '<p class="state-hint" style="margin-top:12px"><a href="/" style="color:#2563eb">Back to sign in</a></p>'
      + '</div>';
  });

  document.getElementById('back-to-login').addEventListener('click', (e) => {
    e.preventDefault();
    renderLoginForm(root);
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Password reset landing (KAN-858) — handles ?token_hash=...&type=recovery
// ────────────────────────────────────────────────────────────────────────────

async function renderPasswordResetForm(root, tokenHash) {
  renderLoading(root);

  const { error: verifyErr } = await _sb.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' });
  if (verifyErr) {
    renderError(root, null, 'This reset link is invalid or has expired. Please request a new one.');
    return;
  }

  root.innerHTML =
    '<header class="portal-header">'
    + '<div class="portal-logo"><img src="/logo.svg" alt="Supersede" width="36" height="36"><span class="portal-brand">Supersede</span></div>'
    + '<h1 class="portal-title">Shipper Portal</h1>'
    + '</header>'
    + '<section class="portal-card" style="max-width:420px;margin:0 auto" aria-label="Set new password">'
    + '<h2 class="section-heading">Set New Password</h2>'
    + '<form id="reset-pw-form" class="quote-form" novalidate>'
    + '<div class="form-group">'
    + '<label class="form-label" for="new-password">New Password <span class="required">*</span></label>'
    + '<input type="password" id="new-password" class="form-input" placeholder="At least 8 characters" minlength="8" required autocomplete="new-password">'
    + '</div>'
    + '<div class="form-group">'
    + '<label class="form-label" for="new-password2">Confirm Password <span class="required">*</span></label>'
    + '<input type="password" id="new-password2" class="form-input" placeholder="Repeat password" required autocomplete="new-password">'
    + '</div>'
    + '<div class="form-footer">'
    + '<button type="submit" class="btn-submit" id="reset-pw-btn">Set Password</button>'
    + '<div class="submit-error" id="reset-pw-error" role="alert" hidden></div>'
    + '</div>'
    + '</form>'
    + '</section>';

  document.getElementById('reset-pw-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const pw    = document.getElementById('new-password').value;
    const pw2   = document.getElementById('new-password2').value;
    const errEl = document.getElementById('reset-pw-error');
    const btn   = document.getElementById('reset-pw-btn');

    if (pw.length < 8) { errEl.textContent = 'Password must be at least 8 characters.'; errEl.hidden = false; return; }
    if (pw !== pw2)    { errEl.textContent = 'Passwords do not match.'; errEl.hidden = false; return; }
    errEl.hidden = true;
    btn.disabled = true; btn.textContent = 'Saving…';

    const { error: updateErr } = await _sb.auth.updateUser({ password: pw });
    if (updateErr) {
      errEl.textContent = 'Failed to update password. Please try again.';
      errEl.hidden = false;
      btn.disabled = false; btn.textContent = 'Set Password';
      return;
    }
    history.replaceState(null, '', '/');
    await renderHistory(root);
  });
}

// ────────────────────────────────────────────────────────────────────────────
// RFQ history view (authenticated portal users)
// ────────────────────────────────────────────────────────────────────────────

async function renderHistory(root) {
  renderLoading(root);

  // Load shipper profile (for name and history_months setting)
  const { data: shipper } = await _sb
    .from('shippers')
    .select('id, name, email, history_months, allow_history_access')
    .single();

  if (!shipper || !shipper.allow_history_access) {
    root.innerHTML =
      '<div class="portal-card portal-state-card">'
      + '<div class="portal-logo"><img src="/logo.svg" alt="Supersede" width="40" height="40"><span class="portal-brand">Supersede</span></div>'
      + '<div class="state-icon" style="font-size:36px;margin:0 auto 12px">&#128274;</div>'
      + '<h2 class="state-heading">History Not Available</h2>'
      + '<p class="state-msg">History access has not been enabled for your account. Please contact Supersede.</p>'
      + '</div>';
    return;
  }

  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - (shipper.history_months || 1));

  const { data: recipients } = await _sb
    .from('rfq_recipients')
    .select('id, status, quote_amount_usd, quote_notes, responded_at, awarded_at, rfq_id, rfqs(id, serial, status, shipment_snapshot, created_at, expires_at)')
    .gte('created_at', cutoff.toISOString())
    .order('created_at', { ascending: false });

  const rows = (recipients || []).map(r => {
    const rfq  = r.rfqs || {};
    const snap = rfq.shipment_snapshot || {};
    const outcome = r.status === 'awarded'
      ? '<span style="color:#16a34a;font-weight:700">&#10003; Won</span>'
      : (rfq.status === 'awarded' || rfq.status === 'closed')
      ? '<span style="color:#dc2626">&#10005; Not selected</span>'
      : '<span style="color:#92400e">Pending</span>';
    const quote = r.quote_amount_usd != null
      ? '$' + Number(r.quote_amount_usd).toFixed(2)
      : '—';
    return '<tr>'
      + '<td>' + escHtml(rfq.serial || rfq.id || '—') + '</td>'
      + '<td>' + escHtml(snap.destination_address || snap.dest_address || '—') + '</td>'
      + '<td>' + (rfq.created_at ? fmtDate(rfq.created_at) : '—') + '</td>'
      + '<td>' + quote + '</td>'
      + '<td>' + outcome + '</td>'
      + '</tr>';
  }).join('');

  const total     = recipients?.length || 0;
  const won       = (recipients||[]).filter(r => r.status === 'awarded').length;
  const responded = (recipients||[]).filter(r => r.quote_amount_usd != null).length;

  root.innerHTML =
    '<header class="portal-header">'
    + '<div class="portal-logo"><img src="/logo.svg" alt="Supersede" width="36" height="36"><span class="portal-brand">Supersede</span></div>'
    + '<h1 class="portal-title">Quote History &mdash; ' + escHtml(shipper.name || shipper.email) + '</h1>'
    + '<button id="history-signout" style="margin-left:auto;background:none;border:1px solid #e2e8f0;border-radius:6px;padding:4px 12px;cursor:pointer;font-size:12px;color:#64748b">Sign Out</button>'
    + '</header>'
    + '<div style="display:flex;gap:16px;margin-bottom:20px;flex-wrap:wrap">'
    + '<div class="portal-card" style="flex:1;min-width:120px;text-align:center;padding:16px">'
    + '<div style="font-size:28px;font-weight:700;color:#1e293b">' + total + '</div>'
    + '<div style="font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.8px">RFQs Invited</div>'
    + '</div>'
    + '<div class="portal-card" style="flex:1;min-width:120px;text-align:center;padding:16px">'
    + '<div style="font-size:28px;font-weight:700;color:#16a34a">' + won + '</div>'
    + '<div style="font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.8px">Won</div>'
    + '</div>'
    + '<div class="portal-card" style="flex:1;min-width:120px;text-align:center;padding:16px">'
    + '<div style="font-size:28px;font-weight:700;color:#2563eb">'
    + (total > 0 ? Math.round((won / total) * 100) : 0) + '%'
    + '</div>'
    + '<div style="font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.8px">Win Rate</div>'
    + '</div>'
    + '</div>'
    + '<section class="portal-card" aria-label="RFQ history">'
    + '<h2 class="section-heading">Last ' + (shipper.history_months || 1) + ' month' + ((shipper.history_months || 1) !== 1 ? 's' : '') + ' of activity</h2>'
    + (rows
      ? '<div style="overflow-x:auto"><table class="detail-table" style="width:100%">'
        + '<thead><tr><th>RFQ #</th><th>Destination</th><th>Date</th><th>Your Quote</th><th>Outcome</th></tr></thead>'
        + '<tbody>' + rows + '</tbody></table></div>'
      : '<p class="state-hint">No RFQ activity in this period.</p>')
    + '</section>'
    + '<p class="closure-note" style="text-align:center">Showing last ' + (shipper.history_months||1) + ' month' + ((shipper.history_months||1)!==1?'s':'') + ' per your account settings.</p>';

  document.getElementById('history-signout').addEventListener('click', async () => {
    await _sb.auth.signOut();
    renderLoginForm(root);
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Utilities
// ────────────────────────────────────────────────────────────────────────────

function escHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

function fmtPieces(snap) {
  const v = snap.pieces ?? snap.pallet_count;
  return v != null ? String(v) : '—';
}

function fmtWeight(snap) {
  const v = snap.total_weight_lbs ?? snap.weight_lbs ?? snap.weight;
  if (v == null) return '—';
  return typeof v === 'number' ? v.toLocaleString('en-US') + ' lbs' : String(v);
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
  } catch { return dateStr; }
}

function fmtDatetime(date) {
  try {
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
  } catch { return String(date); }
}

function getCountdownText(expiresAt) {
  const diffMs = expiresAt.getTime() - Date.now();
  if (diffMs <= 0) return 'Deadline has passed.';
  const totalMins = Math.floor(diffMs / 60000);
  const days  = Math.floor(totalMins / 1440);
  const hours = Math.floor((totalMins % 1440) / 60);
  const mins  = totalMins % 60;
  const parts = [];
  if (days  > 0) parts.push(days  + ' day'    + (days  === 1 ? '' : 's'));
  if (hours > 0) parts.push(hours + ' hour'   + (hours === 1 ? '' : 's'));
  if (days  < 1) parts.push(mins  + ' minute' + (mins  === 1 ? '' : 's'));
  return parts.join(' ') + ' remaining';
}

// ────────────────────────────────────────────────────────────────────────────
// Bootstrap
// ────────────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', async () => {
  mountBuildStamp();   // idempotent — also called at module load, before anything that can throw

  // KAN-825: without config there is no client. Say so, instead of running on and failing obscurely.
  if (!_configOk) { _renderFatalConfigError(); return; }

  const root = document.getElementById('shipper-app');

  // Check for existing authenticated session first
  const { data: { session } } = await _sb.auth.getSession();
  if (session) {
    await renderHistory(root);
    return;
  }

  const params = new URLSearchParams(window.location.search);

  // KAN-858: PKCE password recovery landing — must check before the magic-link token branch
  const tokenHash = params.get('token_hash');
  const linkType  = params.get('type');
  if (tokenHash && linkType === 'recovery') {
    await renderPasswordResetForm(root, tokenHash);
    return;
  }

  const token = params.get('token');

  if (!token) {
    renderLoginForm(root);
    return;
  }

  renderLoading(root);

  let data;
  try {
    const resp = await fetch(SUPABASE_URL + '/functions/v1/validate-rfq-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    // validate-rfq-token always returns a JSON body — including on 400/401/403/404/500
    // error responses (VOIDED, LINK_EXPIRED, CANCELLED, EXPIRED, NOT_FOUND, ...). Parse
    // it regardless of resp.ok so those specific error codes reach renderError() below
    // instead of falling through to the generic network-error message. A prior
    // `resp.status !== 422` gate here never matched any status this function actually
    // returns, so every server-side error rendered as "Network error" — fixed as part
    // of KAN-590/KAN-592 since both add new error codes that depend on this working.
    data = await resp.json();
  } catch {
    renderError(root, null, 'Network error. Please check your connection and try again.');
    return;
  }

  if (data.error) {
    renderError(root, data.error.code || data.error, null);
    return;
  }

  const { rfq, recipient, previousSnapshot, shipperEmail, quoteHistory } = data;

  if (recipient && recipient.responded_at) {
    renderAlreadyResponded(root, recipient.quote_amount_usd, shipperEmail, quoteHistory);
    return;
  }

  renderQuoteForm(root, rfq, recipient, previousSnapshot || null, token, shipperEmail, quoteHistory);
});
